import { Box, makeStyles, Typography } from "@material-ui/core";
import * as React from "react";

import { fontFamilies } from "@/designTokens";
import theme from "@/theme";

const ROW_HEIGHT = 44;
const useStyles = makeStyles(() => ({
  columns: {
    display: "flex",
    position: "relative",
    borderRadius: 8,
    // 各列のoverflowで切る。親のhiddenはSwipeableDrawerの内部スクロール判定を消してしまう。
    "&::before": {
      content: "\"\"",
      position: "absolute",
      top: 66,
      left: 0,
      right: 0,
      height: ROW_HEIGHT,
      backgroundColor: theme.palette.semantic["state/selected-bg"],
      borderRadius: 8,
      pointerEvents: "none",
    },
  },
  column: {
    flex: 1,
    height: 176,
    overflowY: "auto",
    overscrollBehavior: "contain",
    scrollSnapType: "y mandatory",
    scrollbarWidth: "none",
    position: "relative",
    padding: "66px 0",
    "&::-webkit-scrollbar": { display: "none" },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  },
  option: {
    scrollSnapAlign: "center",
    height: ROW_HEIGHT,
    width: "100%",
    display: "flex",
    gap: 3,
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    background: "none",
    cursor: "pointer",
    color: theme.palette.semantic["icon/muted"],
    fontFamily: fontFamilies.number,
    fontSize: 19,
    padding: 0,
  },
  selected: { color: theme.palette.text.primary, fontSize: 24, fontWeight: 600 },
  unit: { fontSize: 14, color: theme.palette.text.secondary, fontFamily: fontFamilies.body, fontWeight: 400 },
  echo: {
    fontFamily: fontFamilies.number,
    color: theme.palette.primary.main,
    fontSize: 12,
    textAlign: "center",
    marginTop: 12,
    minHeight: 18,
  },
}));

export type WheelColumn = {
  label: string;
  values: number[];
  value?: number | null;
  initialValue: number;
  unit: string;
  onChange: (value: number) => void;
};

/** 初期スクロールは回答ではない。操作した列だけonChangeで確定する。 */
const Column: React.FC<WheelColumn> = ({ label, values, value, initialValue, unit, onChange }) => {
  const classes = useStyles();
  const ref = React.useRef<HTMLDivElement>(null);
  const scrolling = React.useRef(false);
  const timer = React.useRef<number>();
  const index = Math.max(0, values.indexOf(value ?? initialValue));
  React.useEffect(() => {
    scrolling.current = false;
    if (ref.current) ref.current.scrollTop = index * ROW_HEIGHT;
    return () => window.clearTimeout(timer.current);
  }, [index]);
  const select = (next: number) => {
    onChange(values[Math.max(0, Math.min(values.length - 1, next))]);
  };
  return (
    <div
      ref={ref}
      className={classes.column}
      role="listbox"
      tabIndex={0}
      aria-label={label}
      aria-activedescendant={`${label}-${values[index]}`}
      onWheel={() => {
        scrolling.current = true;
      }}
      onTouchStart={() => {
        scrolling.current = true;
      }}
      onPointerDown={() => {
        scrolling.current = true;
      }}
      onScroll={() => {
        if (!scrolling.current) return;
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          scrolling.current = false;
          if (ref.current) select(Math.round(ref.current.scrollTop / ROW_HEIGHT));
        }, 150);
      }}
      onKeyDown={(e) => {
        if (["ArrowDown", "ArrowUp", "Home", "End", "Enter", " "].includes(e.key)) {
          e.preventDefault();
          select(
            e.key === "ArrowDown"
              ? index + 1
              : e.key === "ArrowUp"
                ? index - 1
                : e.key === "Home"
                  ? 0
                  : e.key === "End"
                    ? values.length - 1
                    : index
          );
        }
      }}
    >
      {values.map((n, i) => (
        <button
          type="button"
          tabIndex={-1}
          role="option"
          aria-selected={value === n}
          id={`${label}-${n}`}
          key={n}
          className={`${classes.option} ${i === index ? classes.selected : ""}`}
          onClick={() => select(i)}
        >
          {n}
          <span className={classes.unit}>{unit}</span>
        </button>
      ))}
    </div>
  );
};

export const WheelPicker: React.FC<{ columns: WheelColumn[]; echo?: string }> = ({ columns, echo }) => {
  const classes = useStyles();
  return (
    <Box style={{ maxWidth: 342, margin: "0 auto" }}>
      <Box className={classes.columns}>
        {columns.map((column) => (
          <Column key={column.label} {...column} />
        ))}
      </Box>
      <Typography className={classes.echo}>{echo || "\u00a0"}</Typography>
    </Box>
  );
};
